/**
 * HowItWorksBlock
 * ---------------------------------------------------------------------------
 * A compact, horizontal process strip that explains how to work with LuxRally
 * in 4 numbered steps. Blends process with benefit messaging.
 *
 * Layout: 4-column grid on desktop, 2×2 on tablet, stacked on mobile.
 * Supports ivory (default) and teal background via CMS toggle.
 */

import { useInquiryModal, type InquiryContext } from '@/components/inquiry/InquiryModal';
import { cn } from '@/lib/utils';
import { motion, useReducedMotion } from 'framer-motion';
import React from 'react';
import { withBlockRegistration } from '../withBlockRegistration';

// ── Types ──────────────────────────────────────────────────────────

type Background = 'ivory' | 'teal';

interface StepItem {
    iconName?: string;
    stepNumber: string;
    title: string;
    description: string;
}

interface HowItWorksBlockProps {
    id?: number | string;
    rawContent?: Record<string, unknown>;
    settings?: {
        background?: Background;
        showHeader?: boolean;
        showCta?: boolean;
    };
    isEditing?: boolean;
    selectedElement?: string | null;
    onSelectElement?: (elementPath: string) => void;
}

// ── Default steps (non-empty CMS preview) ─────────────────────────

const DEFAULT_STEPS: StepItem[] = [
    {
        stepNumber: '01',
        title: 'Tell Us About Your Trip',
        description:
            'Fill out a short form — dates, destination, and budget. Your personal advisor reaches out by call or text within minutes, never a call center.',
    },
    {
        stepNumber: '02',
        title: 'Get Your Quote in Minutes',
        description:
            'Your advisor sends a personalized quote for hotels and flights — at rates better than booking direct. Once locked in, they layer in tours, restaurants, and every last detail.',
    },
    {
        stepNumber: '03',
        title: 'Secure with One Simple Auth',
        description:
            'A quick credit card authorization is all it takes. From there, booking is as easy as shooting your advisor a text on their personal cell.',
    },
    {
        stepNumber: '04',
        title: 'Travel. We Handle Everything Else.',
        description:
            "Your advisor's personal number is your direct line — call or text 24/7. No chatbots, no hold music, no fees. Our service is entirely complimentary.",
    },
];

// ── Color resolver ─────────────────────────────────────────────────

function resolveColors(isDark: boolean) {
    return {
        bg: isDark ? 'bg-[#0e2727]' : 'bg-[#fdfaf3]',
        heading: isDark ? 'text-[#F5F5F5]' : 'text-[#0e2727]',
        kicker: isDark ? 'text-[#F5F5F5]/60' : 'text-[#0e2727]/60',
        body: isDark ? 'text-[#F5F5F5]/75' : 'text-[#0e2727]/75',
        stepNumber: isDark ? 'text-[#F5F5F5]' : 'text-[#0e2727]',
        numberBg: isDark ? 'bg-[#0e2727]' : 'bg-[#fdfaf3]',
        connector: isDark ? 'bg-[#F5F5F5]/15' : 'bg-[#0e2727]/15',
        ctaBorder: isDark ? 'border-[#F5F5F5]/30 hover:border-[#F5F5F5]/50' : 'border-[#0e2727]/30 hover:border-[#0e2727]/50',
        ctaText: isDark ? 'text-[#F5F5F5]' : 'text-[#0e2727]',
    };
}

// ── Animation ──────────────────────────────────────────────────────

const EASE_CUSTOM: [number, number, number, number] = [0.22, 1, 0.36, 1];

const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.12,
            delayChildren: 0.2,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, ease: EASE_CUSTOM },
    },
};

// ── Component ──────────────────────────────────────────────────────

const HowItWorksBlock = React.forwardRef<HTMLDivElement, HowItWorksBlockProps>(
    ({ rawContent = {}, settings = {}, isEditing = false, selectedElement, onSelectElement }: HowItWorksBlockProps, ref) => {
        const prefersReducedMotion = useReducedMotion();
        const { openInquiry } = useInquiryModal();

        const inquirySource = (rawContent.inquirySource as InquiryContext['source']) || 'homepage_how_it_works';

        const handleTriggerClick = () => {
            openInquiry({
                source: inquirySource,
                page_url: typeof window !== 'undefined' ? window.location.href : '',
            });
        };

        const { background = 'ivory', showHeader = true, showCta = true } = settings;

        const isDark = background === 'teal';
        const colors = resolveColors(isDark);

        // ── Extract rawContent fields ──
        const kicker = (rawContent.kicker as string) || 'HOW IT WORKS';
        const heading = (rawContent.heading as string) || 'Four Steps to Your Perfect Trip';
        const items = (rawContent.items as StepItem[] | undefined) || DEFAULT_STEPS;
        const ctaText = (rawContent.ctaText as string) || 'Book through LuxRally';

        // ── Edit-mode selection helper ──
        const handleSelect = (path: string, e: React.MouseEvent) => {
            if (!isEditing) return;
            e.preventDefault();
            e.stopPropagation();
            onSelectElement?.(path);
        };

        // ── Motion props (respects reduced motion) ──
        const motionContainer = prefersReducedMotion
            ? {}
            : {
                  variants: containerVariants,
                  initial: 'hidden' as const,
                  whileInView: 'visible' as const,
                  viewport: { once: true, margin: '-60px' },
              };

        const motionItem = prefersReducedMotion ? {} : { variants: itemVariants };

        const headerMotion = prefersReducedMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 12 },
                  whileInView: { opacity: 1, y: 0 },
                  viewport: { once: true },
                  transition: { duration: 0.7, ease: EASE_CUSTOM },
              };

        return (
            <section
                ref={ref}
                className={cn('lux-space-lg nav-px w-full', colors.bg, isEditing && 'ring-1 ring-primary/30')}
                onClick={(e) => handleSelect('root', e)}
            >
                <div className="mx-auto max-w-[1600px]">
                    {/* ── Section header — centered ── */}
                    {showHeader && (
                        <motion.div {...headerMotion} className="mb-16 flex flex-col items-center text-center md:mb-20">
                            <p
                                className={cn(
                                    'lux-caps lux-caps-sm mb-3',
                                    colors.kicker,
                                    isEditing && selectedElement === 'kicker' && 'ring-2 ring-primary',
                                )}
                                onClick={(e) => handleSelect('kicker', e)}
                            >
                                {kicker}
                            </p>
                            <h2
                                className={cn(
                                    'lux-didot lux-didot-lg',
                                    colors.heading,
                                    isEditing && selectedElement === 'heading' && 'ring-2 ring-primary',
                                )}
                                onClick={(e) => handleSelect('heading', e)}
                            >
                                {heading}
                            </h2>
                        </motion.div>
                    )}

                    {/* ── Steps grid ── */}
                    <motion.div {...motionContainer} className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
                        {items.map((step, idx) => {
                            const isLast = idx === items.length - 1;

                            return (
                                <motion.div
                                    key={`step-${idx}`}
                                    {...motionItem}
                                    className={cn(
                                        'relative flex flex-col items-center text-center',
                                        isEditing && selectedElement === `items.${idx}` && 'rounded ring-2 ring-primary',
                                    )}
                                    onClick={(e) => handleSelect(`items.${idx}`, e)}
                                >
                                    {/* Didot step number on a horizontal rule */}
                                    <div className="relative flex w-full items-center justify-center">
                                        {/* Connector line — desktop only, runs through vertical midline of number; full cell width, visually broken by the number's background mask */}
                                        {!isLast && (
                                            <div
                                                className={cn(
                                                    'absolute top-1/2 left-1/2 hidden h-px w-full -translate-y-1/2 lg:block',
                                                    colors.connector,
                                                )}
                                                aria-hidden
                                            />
                                        )}
                                        <span
                                            className={cn(
                                                'lux-didot lux-didot-lg relative z-10 inline-block px-5 leading-none',
                                                colors.stepNumber,
                                                colors.numberBg,
                                            )}
                                        >
                                            {step.stepNumber}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h3 className={cn('lux-didot lux-didot-md mt-5 px-2', colors.heading)}>{step.title}</h3>

                                    {/* Description */}
                                    <p className={cn('lux-body lux-body-sm mt-2 max-w-[260px] opacity-75', colors.body)}>{step.description}</p>
                                </motion.div>
                            );
                        })}
                    </motion.div>

                    {/* ── Inquiry trigger ── */}
                    {showCta && (
                        <div className="mt-16 flex flex-col items-center md:mt-20">
                            {isEditing ? (
                                <span
                                    className={cn(
                                        'flex flex-col items-center gap-2',
                                        isDark ? 'text-[#F5F5F5]/40' : 'text-[#0e2727]/40',
                                        selectedElement === 'cta' && 'ring-2 ring-primary',
                                    )}
                                    onClick={(e) => handleSelect('cta', e)}
                                >
                                    <span className="lux-caps lux-caps-sm">{ctaText}</span>
                                    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleTriggerClick}
                                    className={cn(
                                        'group flex items-center gap-2 transition-opacity duration-300',
                                        isDark ? 'text-[#F5F5F5]/40 hover:text-[#F5F5F5]/60' : 'text-[#0e2727]/40 hover:text-[#0e2727]/60',
                                    )}
                                >
                                    <span className="lux-caps lux-caps-sm">{ctaText}</span>
                                    <svg
                                        className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                    >
                                        <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </section>
        );
    },
);

HowItWorksBlock.displayName = 'HowItWorksBlock';

export default withBlockRegistration(HowItWorksBlock, 'how_it_works');
